// Chain navigation history — backs the player's prev/next arrows.
//
// The chain is a forward sequence the viewer steps through (each advance is an
// in-place swap to a recommendation). This module records the experiences
// visited in order, with a pointer at the current one, so the arrows can step
// back and forward. Position is inferred from the experience id as each one
// loads (recordCurrent), so back/forward/new-branch are detected without the
// caller having to say which — one code path (CHAIN_ADVANCE) drives them all.
import type { ChainRecommendation, ExperienceId } from "@/types/experience";

/** Poster and title of a visited experience, for the arrows' hover previews. */
export interface ChainNavMeta {
  thumbUrl?: string | null;
  title?: string;
}

let ids: ExperienceId[] = []; // experience ids visited, in order
let pos = -1; // index of the current experience in ids
const metaById: Record<string, ChainNavMeta> = {}; // id → { thumbUrl, title } for the hover previews

// Record the experience that just became current and update the pointer:
//   • same id            → no-op (re-render / reload of the current video)
//   • the previous id    → a "previous" step: move the pointer back
//   • the next id        → a "next" step over already-visited history: forward
//   • anything else      → a new branch: drop the forward tail and append
//
// Position is INFERRED from the id, not told to us: this is the single
// chokepoint for every experience load (initial, next, prev, and any non-chain
// navigation), so the caller's direction isn't available here. The inference
// rests on one invariant — the recommender never offers an already-visited
// experience as a pick — which the /chain request enforces: exclude_ids carries
// the watched history and the engine drops watched items by id AND title (see
// ChainSession). Under that invariant the only id that can equal ids[pos - 1] is
// a genuine back-step.
//
// Known limitation if that invariant is ever violated (recommender loops back
// to the viewer's immediate predecessor id): that forward move would be read as
// a step back, and a subsequent branch could then trim history from the wrong
// point. Narrow and guarded upstream — documented rather than worked around,
// since disambiguating it would need a direction signal this call site lacks.
//
// `meta` ({ thumbUrl, title }) is stashed per id so the prev arrow's hover
// preview can show a video it has already left (its poster/title aren't in the
// current chain, which only lists what's ahead). Recorded before the pointer
// logic so re-visits refresh it.
export function recordCurrent(
  id: ExperienceId | null | undefined,
  meta?: ChainNavMeta,
) {
  if (id == null) {
    return;
  }
  if (meta) {
    metaById[id] = meta;
  }
  if (pos >= 0 && ids[pos] === id) {
    return;
  }
  if (pos > 0 && ids[pos - 1] === id) {
    pos -= 1;
    return;
  }
  if (pos < ids.length - 1 && ids[pos + 1] === id) {
    pos += 1;
    return;
  }
  ids = ids.slice(0, pos + 1);
  ids.push(id);
  pos = ids.length - 1;
}

export function canPrev() {
  return pos > 0;
}

// The experience to step back to, or null at the start of the history.
export function prevId(): ExperienceId | null {
  return canPrev() ? ids[pos - 1] : null;
}

function hasVisitedNext() {
  return pos >= 0 && pos < ids.length - 1;
}

// The experience to step forward to: the already-visited next one if the
// viewer has stepped back, otherwise the top chain recommendation. `chain` is
// chainStore.getChain() (may be empty). Null when there's nothing ahead.
export function nextId(
  chain: ChainRecommendation[] | null | undefined,
): ExperienceId | null {
  if (hasVisitedNext()) {
    return ids[pos + 1];
  }
  const pick = (chain || [])[0];
  return pick ? pick.experience_id : null;
}

export function canNext(
  chain: ChainRecommendation[] | null | undefined,
): boolean {
  return nextId(chain) != null;
}

// Whether the current experience participates in a recommendation chain at all
// — true when there's somewhere to step in either direction: a
// recommendation ahead, or visited history behind. A standalone video whose
// publisher isn't chain-enabled gets an empty chain and never accrues history,
// so this stays false and the prev/next arrows are hidden entirely (rather than
// shown-but-disabled, which is the in-chain end-of-chain state).
export function inChain(
  chain: ChainRecommendation[] | null | undefined,
): boolean {
  return canPrev() || canNext(chain);
}

// Poster/title recorded for a visited experience, or null. Used by the prev
// arrow's hover preview (and the next arrow when stepping back over history).
export function metaFor(
  id: ExperienceId | null | undefined,
): ChainNavMeta | null {
  return (id != null && metaById[id]) || null;
}
